import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Compass, Loader2, ArrowUpRight, ChevronRight, Award, TrendingUp,
  Target, Clock, Lightbulb, GraduationCap, X, Info, Bookmark, BookmarkCheck,
  Download, BellRing,
} from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { PageShell } from "@/components/altiora/PremiumUI";

const NAVY = "#0B2545";
const CHAMPAGNE = "#C5A880";
const GREEN = "#0B7A3B";
const AMBER = "#7A5B14";
const RED = "#B91C1C";
const IVORY = "#FBF6EA";

function CompatBadge({ pct }) {
  const color = pct >= 75 ? GREEN : pct >= 55 ? AMBER : RED;
  return (
    <div className="flex items-center gap-2">
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-[13px]"
           style={{ background: color }}>{Math.round(pct)}%</div>
    </div>
  );
}

function ChipList({ items, tone, accent, tid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={tid}>
      {items.map((s, i) => (
        <span key={i}
              className="text-[11px] px-2 py-0.5 rounded"
              style={{ background: accent, color: tone,
                          border: `1px solid ${tone}44` }}>
          {s.label_fr || s}
        </span>
      ))}
    </div>
  );
}

export default function AbiCareer() {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [watchIds, setWatchIds] = useState(new Set());
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  const loadWatchAndAlerts = async () => {
    try {
      const [w, a] = await Promise.all([
        api.get("/abi/copilot/career/watchlist"),
        api.get("/abi/copilot/career/alerts?only_new=true"),
      ]);
      setWatchIds(new Set(w.data.items.map(i => i.opportunity_id)));
      setAlerts(a.data.alerts || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    Promise.all([
      api.get("/abi/copilot/career/opportunities?limit=12&min_compat=0"),
      api.get("/abi/copilot/career/profile"),
    ]).then(([o, p]) => {
      setData(o.data);
      setProfile(p.data);
    }).catch(() => toast.error("Erreur de chargement du moteur d'opportunités."))
      .finally(() => setLoading(false));
    loadWatchAndAlerts();
  }, []);

  const openDetail = async (opp) => {
    setSelected(opp);
    setDetailLoading(true);
    try {
      const r = await api.get(`/abi/copilot/career/opportunities/${opp.opportunity_id}`);
      setDetail(r.data);
    } catch { toast.error("Détail indisponible."); }
    setDetailLoading(false);
  };

  const toggleWatch = async (oppId, ev) => {
    ev?.stopPropagation();
    try {
      if (watchIds.has(oppId)) {
        await api.delete(`/abi/copilot/career/watchlist/${oppId}`);
        toast.success("Retiré de votre liste de suivi.");
      } else {
        await api.post("/abi/copilot/career/watchlist", { opportunity_id: oppId });
        toast.success("Ajouté — vous serez alerté à chaque palier franchi.");
      }
      loadWatchAndAlerts();
    } catch { toast.error("Opération impossible."); }
  };

  const downloadPdf = async (oppId, ev) => {
    ev?.stopPropagation();
    try {
      const r = await api.get(`/abi/copilot/career/opportunities/${oppId}/pdf`,
                                { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `altiora_${oppId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Génération PDF impossible."); }
  };

  const markAlertRead = async (alertId) => {
    try {
      await api.post(`/abi/copilot/career/alerts/${alertId}/read`);
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
    } catch { /* silent */ }
  };

  if (loading) return (
    <div className="p-8 flex items-center gap-2" data-testid="abi-career-loading">
      <Loader2 size={16} className="animate-spin" /> Chargement des opportunités…
    </div>
  );

  return (
    <PageShell testid="abi-career-root">
      <TopBar />

      {/* Hero */}
      <div className="px-8 pt-8 pb-6"
           style={{ background: `linear-gradient(120deg,${NAVY} 0%,#123B6E 60%,#1D5AA5 100%)` }}>
        <div className="max-w-6xl mx-auto text-white">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest opacity-80">
            <Compass size={14} /> ALTIORA · Détection d'opportunités professionnelles
          </div>
          <h1 className="text-3xl md:text-4xl font-serif mt-2 leading-tight"
              data-testid="abi-career-title">
            Trois métiers auxquels votre parcours <em style={{ color: "#F5D687" }}>vous prépare déjà.</em>
          </h1>
          <p className="mt-3 text-white/85 max-w-2xl text-[14px]">
            ALTIORA ne vous enferme pas dans votre métier actuel. Voici les
            trajectoires professionnelles auxquelles votre évolution vous
            donne aujourd'hui accès — même celles que vous n'aviez jamais envisagées.
          </p>

          {/* Alerts badge */}
          {alerts.length > 0 && (
            <button onClick={() => setShowAlerts(true)}
                    data-testid="abi-career-alerts-badge"
                    className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded transition hover:opacity-90"
                    style={{ background: "#F5D687", color: "#0B2545", fontWeight: 600, fontSize: 12 }}>
              <BellRing size={14} /> {alerts.length} palier{alerts.length > 1 ? "s" : ""} franchi{alerts.length > 1 ? "s" : ""} — voir
            </button>
          )}

          <div className="mt-6 grid md:grid-cols-4 gap-3">
            <div className="rounded-lg p-3" style={{ background: "rgba(197,168,128,0.20)" }}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Profession actuelle</div>
              <div className="text-base font-serif mt-1" style={{ color: "#F5D687" }}
                   data-testid="abi-career-profession">
                {profile?.profession_label_fr || "—"}
              </div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.10)" }}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Métiers scannés</div>
              <div className="text-2xl font-serif mt-1 font-bold">{data?.total_scanned || 0}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.10)" }}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Opportunités révélées</div>
              <div className="text-2xl font-serif mt-1 font-bold"
                   data-testid="abi-career-returned">{data?.returned || 0}</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: "rgba(255,255,255,0.10)" }}>
              <div className="text-[10px] uppercase tracking-widest opacity-70">Richesse des données</div>
              <div className="text-2xl font-serif mt-1 font-bold">{data?.data_richness_pct || 0}<span className="text-sm opacity-60">/100</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile snapshot */}
      {profile && (
        <div className="max-w-6xl mx-auto px-8 pt-6">
          <div className="bg-white rounded-lg shadow-sm p-5" data-testid="abi-career-profile-summary">
            <div className="flex items-center gap-2 mb-3">
              <Award size={16} style={{ color: CHAMPAGNE }} />
              <h3 className="font-serif text-lg" style={{ color: NAVY }}>Votre capital professionnel actuel</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: GREEN }}>
                  Acquis · {profile.acquired_skills.length}
                </div>
                <ChipList items={profile.acquired_skills.slice(0, 8)} tone={GREEN} accent="#DCFCE7" tid="abi-career-acquired-chips" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: AMBER }}>
                  Transférables · {profile.transferable_skills.length}
                </div>
                <ChipList items={profile.transferable_skills.slice(0, 8)} tone={AMBER} accent="#FBF6EA" tid="abi-career-transferable-chips" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: NAVY }}>
                  Habitudes construites
                </div>
                <ul className="text-[12px] text-slate-700 space-y-0.5">
                  {(profile.habits_won || []).slice(0, 4).map((h, i) =>
                    <li key={i}>· {h}</li>)}
                  {(profile.habits_won || []).length === 0 && (
                    <li className="italic text-slate-500">Les habitudes confirmées apparaîtront ici.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opportunities list */}
      <div className="max-w-6xl mx-auto px-8 py-6">
        {(!data || data.opportunities.length === 0) ? (
          <div className="p-6 rounded bg-white text-slate-700 text-sm" data-testid="abi-career-empty">
            Le moteur n'a pas encore assez de signaux pour vous proposer d'opportunités.
            Utilisez ABI régulièrement pendant quelques jours — les recommandations
            arriveront naturellement.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="abi-career-opportunities-list">
            {data.opportunities.map((o) => (
              <div key={o.opportunity_id}
                   className="bg-white rounded-lg shadow-sm p-5 flex flex-col cursor-pointer hover:shadow-md transition"
                   onClick={() => openDetail(o)}
                   data-testid={`abi-career-opp-${o.opportunity_id}`}>
                <div className="flex items-start gap-4">
                  <CompatBadge pct={o.compatibility_pct} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: CHAMPAGNE }}>
                      {o.sector_fr}
                    </div>
                    <h4 className="font-serif text-lg mt-0.5" style={{ color: NAVY }}>{o.title_fr}</h4>
                    <div className="flex items-center gap-3 text-[11px] mt-1 text-slate-600">
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {o.time_to_ready_months} mois</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Info size={11} /> Confiance {Math.round(o.confidence_pct)}%</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <button onClick={(e) => toggleWatch(o.opportunity_id, e)}
                            data-testid={`abi-career-watch-${o.opportunity_id}`}
                            title={watchIds.has(o.opportunity_id) ? "Retirer du suivi" : "Ajouter au suivi"}
                            className="p-1.5 rounded hover:bg-slate-100 transition">
                      {watchIds.has(o.opportunity_id)
                        ? <BookmarkCheck size={16} style={{ color: CHAMPAGNE }} />
                        : <Bookmark size={16} className="text-slate-400" />}
                    </button>
                    <button onClick={(e) => downloadPdf(o.opportunity_id, e)}
                            data-testid={`abi-career-pdf-${o.opportunity_id}`}
                            title="Télécharger le rapport PDF"
                            className="p-1.5 rounded hover:bg-slate-100 transition">
                      <Download size={15} className="text-slate-400" />
                    </button>
                  </div>
                </div>
                <p className="mt-3 text-[13px] text-slate-700 leading-relaxed line-clamp-3">
                  {o.reasoning_fr}
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {o.acquired_skills.slice(0, 3).map((s, i) =>
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#DCFCE7", color: GREEN }}>
                      ✓ {s.label_fr}
                    </span>)}
                  {o.transferable_skills.slice(0, 2).map((s, i) =>
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "#FBF6EA", color: AMBER }}>
                      ↔ {s.label_fr}
                    </span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="abi-career-detail-drawer"
             onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="flex-1 bg-black/50" />
          <div className="w-full max-w-2xl bg-white overflow-y-auto shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center gap-3 p-5 border-b bg-white z-10">
              {detail && <CompatBadge pct={detail.compatibility_pct} />}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: CHAMPAGNE }}>
                  {selected.sector_fr}
                </div>
                <h3 className="font-serif text-xl leading-tight" style={{ color: NAVY }}
                    data-testid="abi-career-detail-title">
                  {selected.title_fr}
                </h3>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }}
                      className="p-1 hover:bg-slate-100 rounded" data-testid="abi-career-detail-close">
                <X size={18} />
              </button>
            </div>
            {detail && !detailLoading && (
              <div className="px-5 py-3 border-b flex gap-2 flex-wrap">
                <button onClick={() => toggleWatch(selected.opportunity_id)}
                        data-testid="abi-career-detail-watch"
                        className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded transition"
                        style={{
                          background: watchIds.has(selected.opportunity_id) ? CHAMPAGNE : "#F1F5F9",
                          color: watchIds.has(selected.opportunity_id) ? NAVY : "#334155",
                          fontWeight: 600,
                        }}>
                  {watchIds.has(selected.opportunity_id)
                    ? <><BookmarkCheck size={13}/> Suivi actif — retirer</>
                    : <><Bookmark size={13}/> Ajouter au suivi</>}
                </button>
                <button onClick={() => downloadPdf(selected.opportunity_id)}
                        data-testid="abi-career-detail-pdf"
                        className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded transition"
                        style={{ background: NAVY, color: "#FFFFFF", fontWeight: 600 }}>
                  <Download size={13}/> Rapport PDF (partageable)
                </button>
              </div>
            )}
            {detailLoading ? (
              <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={14} className="animate-spin" /> Chargement du raisonnement…
              </div>
            ) : detail ? (
              <div className="p-6 space-y-5">
                {/* Reasoning */}
                <div className="p-4 rounded border-l-4" style={{ borderLeftColor: CHAMPAGNE, background: "#FBF6EA" }}>
                  <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: CHAMPAGNE }}>
                    Pourquoi cette opportunité vous concerne
                  </div>
                  <p className="text-[13.5px] leading-relaxed text-slate-800"
                     data-testid="abi-career-detail-reasoning">
                    {detail.reasoning_fr}
                  </p>
                </div>

                {/* Skills breakdown */}
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: GREEN }}>
                      Déjà acquises ({detail.acquired_skills.length})
                    </div>
                    <ChipList items={detail.acquired_skills} tone={GREEN} accent="#DCFCE7" tid="abi-career-detail-acquired" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: AMBER }}>
                      Transférables ({detail.transferable_skills.length})
                    </div>
                    <ChipList items={detail.transferable_skills} tone={AMBER} accent="#FBF6EA" tid="abi-career-detail-transferable" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: RED }}>
                      À consolider ({detail.missing_skills.length})
                    </div>
                    <ChipList items={detail.missing_skills} tone={RED} accent="#FEE2E2" tid="abi-career-detail-missing" />
                  </div>
                </div>

                {/* Transition plan */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target size={16} style={{ color: NAVY }} />
                    <h4 className="font-serif text-lg" style={{ color: NAVY }}>Plan de transition personnalisé</h4>
                  </div>
                  <ol className="space-y-2" data-testid="abi-career-detail-plan">
                    {detail.transition_plan_fr.map((s, i) => (
                      <li key={i} className="flex gap-3 p-3 rounded" style={{ background: "#F8FAFC" }}>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0"
                              style={{ background: NAVY }}>{i + 1}</span>
                        <span className="text-[13px] text-slate-800 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Recommended trainings */}
                {detail.recommended_trainings_fr.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap size={16} style={{ color: NAVY }} />
                      <h4 className="font-serif text-lg" style={{ color: NAVY }}>Formations recommandées</h4>
                    </div>
                    <ul className="space-y-1.5 text-[13px]" data-testid="abi-career-detail-trainings">
                      {detail.recommended_trainings_fr.map((t, i) =>
                        <li key={i} className="flex gap-2 text-slate-800"><Lightbulb size={13} className="mt-0.5 flex-shrink-0" style={{ color: CHAMPAGNE }} />{t}</li>)}
                    </ul>
                  </div>
                )}

                {/* Certifications */}
                {detail.useful_certifications_fr.length > 0 && (
                  <div>
                    <h4 className="font-serif text-lg mb-2" style={{ color: NAVY }}>Certifications utiles</h4>
                    <ul className="space-y-1 text-[13px] text-slate-800">
                      {detail.useful_certifications_fr.map((c, i) =>
                        <li key={i}>· {c}</li>)}
                    </ul>
                  </div>
                )}

                {/* Advantages / Constraints / Evolution */}
                <div className="grid md:grid-cols-3 gap-3 text-[12.5px]">
                  <div className="p-3 rounded" style={{ background: "#DCFCE7" }}>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: GREEN }}>Avantages</div>
                    <p className="text-slate-800 leading-relaxed">{detail.advantages_fr}</p>
                  </div>
                  <div className="p-3 rounded" style={{ background: "#FEF3C7" }}>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: AMBER }}>Contraintes</div>
                    <p className="text-slate-800 leading-relaxed">{detail.constraints_fr}</p>
                  </div>
                  <div className="p-3 rounded" style={{ background: "#EEF4FF" }}>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: NAVY }}>Évolutions possibles</div>
                    <p className="text-slate-800 leading-relaxed">{detail.evolution_fr}</p>
                  </div>
                </div>

                {detail.salary_band_ca && (
                  <div className="p-3 rounded text-[13px] flex items-center gap-2"
                       style={{ background: "#F1F5F9" }}>
                    <TrendingUp size={14} style={{ color: NAVY }} />
                    <span className="font-medium text-slate-700">Fourchette de rémunération</span>
                    <span className="ml-auto font-mono font-semibold text-slate-900">{detail.salary_band_ca}</span>
                  </div>
                )}

                <div className="text-[11px] italic text-slate-500 border-t pt-3">
                  Aide à la décision — jamais promesse de résultat. Le moteur observe
                  votre trajectoire ; il ne remplace ni votre jugement, ni un conseiller humain.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Alerts drawer */}
      {showAlerts && (
        <div className="fixed inset-0 z-50 flex" data-testid="abi-career-alerts-drawer"
             onClick={() => setShowAlerts(false)}>
          <div className="flex-1 bg-black/50" />
          <div className="w-full max-w-md bg-white overflow-y-auto shadow-2xl"
               onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center gap-3 p-5 border-b bg-white z-10">
              <BellRing size={18} style={{ color: CHAMPAGNE }} />
              <h3 className="font-serif text-xl" style={{ color: NAVY }}>Alertes de paliers</h3>
              <button onClick={() => setShowAlerts(false)}
                      className="ml-auto p-1 hover:bg-slate-100 rounded"
                      data-testid="abi-career-alerts-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {alerts.length === 0 ? (
                <div className="text-[13px] text-slate-500 italic">
                  Aucun palier récent. Ajoutez des opportunités à votre suivi pour être alerté.
                </div>
              ) : alerts.map(a => (
                <div key={a.alert_id}
                     className="p-4 rounded border-l-4"
                     style={{ borderLeftColor: CHAMPAGNE, background: "#FBF6EA" }}
                     data-testid={`abi-career-alert-${a.alert_id}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: CHAMPAGNE }}>
                    Palier {a.threshold}% franchi
                  </div>
                  <p className="text-[13px] mt-1 text-slate-800 leading-relaxed">{a.text_fr}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[11px] text-slate-500 font-mono">
                      {new Date(a.at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    </span>
                    <button onClick={() => markAlertRead(a.alert_id)}
                            className="text-[11px] text-slate-500 hover:text-slate-900 transition">
                      Marquer comme lue
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
